import { useState, useEffect } from 'react'
import { billingAPI, fmt, fmtFecha } from '../services/api'
import toast from 'react-hot-toast'

const num = v => parseFloat(String(v ?? '').replace(',', '.')) || 0

const ESTADO_INFO = {
  activo:      { label: 'Al día',       color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  por_vencer:  { label: 'Por vencer',   color: '#B8860B',              bg: '#FFF7E0' },
  vencido_hoy: { label: 'Vence hoy',    color: '#B8860B',              bg: '#FFF1D6' },
  suspendido:  { label: 'Suspendida',   color: 'var(--color-danger)',  bg: '#FDECEC' },
}

// Gráfico de barras simple, sin librerías — ingresos realmente cobrados por mes
function BarrasIngresos ({ datos }) {
  if (!datos.length) return <div className="empty-state" style={{padding:30}}><p>Aún no hay pagos registrados</p></div>
  const max = Math.max(...datos.map(d => d.total), 1)
  return (
    <div style={{display:'flex', alignItems:'flex-end', gap:14, height:160, padding:'0 4px'}}>
      {datos.map((d, i) => (
        <div key={i} style={{flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6}}>
          <div style={{fontSize:11, fontWeight:600, color:'var(--color-muted)'}}>{fmt(d.total)}</div>
          <div style={{
            width:'100%', maxWidth:44, borderRadius:'6px 6px 0 0', background:'var(--color-primary)',
            height:`${Math.max((d.total / max) * 110, 4)}px`,
          }}/>
          <div style={{fontSize:11, color:'var(--color-muted)'}}>{d.mes.slice(5)}/{d.mes.slice(2,4)}</div>
        </div>
      ))}
    </div>
  )
}

export default function Suscripciones () {
  const [empresas, setEmpresas] = useState([])
  const [resumen, setResumen]   = useState(null)
  const [historial, setHistorial] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError]       = useState(null)

  const [editando, setEditando] = useState(null)
  const [form, setForm]         = useState({})
  const [guardando, setGuardando] = useState(false)
  const [generandoLink, setGenerandoLink] = useState(null)
  const [linkListo, setLinkListo] = useState(null)

  useEffect(() => { cargar() }, [])

  const cargar = async () => {
    setCargando(true)
    setError(null)
    try {
      const [{ data }, { data: hist }] = await Promise.all([
        billingAPI.listar(), billingAPI.historial(6),
      ])
      setEmpresas(data.empresas || [])
      setResumen(data.resumen || null)
      setHistorial(hist || [])
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'No se pudo cargar la información de suscripciones')
    } finally { setCargando(false) }
  }

  const abrirEdicion = e => {
    setForm({
      monto_mensual: e.monto_mensual || '',
      email_contacto: e.email_contacto || '',
      fecha_proximo_pago: e.fecha_proximo_pago ? e.fecha_proximo_pago.split('T')[0] : '',
    })
    setEditando(e)
  }

  const guardar = async () => {
    setGuardando(true)
    try {
      await billingAPI.actualizar(editando.id, {
        monto_mensual: form.monto_mensual === '' ? null : num(form.monto_mensual),
        email_contacto: form.email_contacto || null,
        fecha_proximo_pago: form.fecha_proximo_pago || null,
      })
      toast.success('Suscripción actualizada')
      setEditando(null)
      cargar()
    } catch {} finally { setGuardando(false) }
  }

  const suspender = async (e) => {
    if (!window.confirm(`¿Suspender el acceso de ${e.nombre} ahora mismo?`)) return
    try { await billingAPI.suspender(e.id); toast.success('Empresa suspendida'); cargar() } catch {}
  }

  const reactivar = async (e) => {
    const dias = window.prompt('¿Por cuántos días activamos el acceso?', '30')
    if (dias === null) return
    try {
      await billingAPI.reactivar(e.id, { dias: parseInt(dias) || 30 })
      toast.success('Empresa reactivada')
      cargar()
    } catch {}
  }

  const generarLink = async (e) => {
    setGenerandoLink(e.id)
    try {
      const { data } = await billingAPI.generarLink(e.id)
      setLinkListo({ empresa: e, url: data.url })
    } catch {} finally { setGenerandoLink(null) }
  }

  const copiarLink = () => {
    navigator.clipboard.writeText(linkListo.url)
    toast.success('Link copiado')
  }

  return (
    <div style={{padding:24, display:'flex', flexDirection:'column', gap:20}}>

      <div>
        <div className="section-title" style={{marginBottom:2}}>Suscripciones</div>
        <div style={{fontSize:13, color:'var(--color-muted)'}}>
          Qué empresas están al día, cuáles debes cobrar, y cuánto factura Redcaja en total
        </div>
      </div>

      {cargando ? (
        <div className="page-loading" style={{height:200}}><div className="spinner"/><span>Cargando...</span></div>
      ) : error ? (
        <div className="card" style={{padding:24, textAlign:'center'}}>
          <p style={{fontWeight:700, color:'var(--color-danger)', marginBottom:6}}>No se pudo cargar Suscripciones</p>
          <p style={{fontSize:13, color:'var(--color-muted)', marginBottom:14}}>{error}</p>
          <button className="btn btn-primary" onClick={cargar}>Reintentar</button>
        </div>
      ) : !resumen ? (
        <div className="card" style={{padding:24, textAlign:'center'}}>
          <p style={{color:'var(--color-muted)'}}>No hay datos todavía.</p>
        </div>
      ) : (
        <>
          {/* Resumen */}
          <div className="metric-grid">
            <div className="metric-card">
              <div className="metric-label">Ingreso mensual (MRR)</div>
              <div className="metric-value" style={{color:'var(--color-primary)'}}>{fmt(resumen.ingreso_mensual_total)}</div>
              <div className="metric-sub">{fmt(resumen.ingreso_activo)} de empresas al día</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">✓ Al día</div>
              <div className="metric-value" style={{color:'var(--color-success)'}}>{resumen.activas}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">⚠ Por vencer / vence hoy</div>
              <div className="metric-value" style={{color:'#B8860B'}}>{resumen.por_vencer + resumen.vencidas_hoy}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">✕ Suspendidas</div>
              <div className="metric-value" style={{color:'var(--color-danger)'}}>{resumen.suspendidas}</div>
            </div>
          </div>

          {/* Gráfico de ingresos reales cobrados */}
          <div className="card">
            <div style={{fontWeight:700, fontSize:15, marginBottom:14}}>Ingresos cobrados (últimos 6 meses)</div>
            <BarrasIngresos datos={historial}/>
          </div>

          {/* Tabla de empresas */}
          <div className="card">
            <div style={{fontWeight:700, fontSize:15, marginBottom:12}}>Empresas · {empresas.length}</div>
            <div className="table-wrap" style={{overflowX:'auto'}}>
              <table style={{minWidth:760}}>
                <thead>
                  <tr>
                    <th>Empresa</th><th>Mensualidad</th><th>Próximo pago</th>
                    <th>Estado</th><th style={{textAlign:'right'}}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {empresas.map(e => {
                    const info = ESTADO_INFO[e.estado]
                    return (
                      <tr key={e.id}>
                        <td>
                          <div style={{fontWeight:600}}>{e.nombre}</div>
                          <div style={{fontSize:12, color:'var(--color-muted)'}}>{e.email_contacto || 'sin correo configurado'}</div>
                        </td>
                        <td>{e.monto_mensual ? fmt(e.monto_mensual) : '—'}</td>
                        <td>
                          {e.fecha_proximo_pago ? fmtFecha(e.fecha_proximo_pago) : '—'}
                          {e.dias !== null && e.estado !== 'suspendido' && (
                            <div style={{fontSize:11, color:'var(--color-muted)'}}>
                              {e.dias === 0 ? 'vence hoy' : e.dias > 0 ? `en ${e.dias} días` : ''}
                            </div>
                          )}
                        </td>
                        <td>
                          <span style={{background:info.bg, color:info.color, fontSize:12, fontWeight:700,
                            padding:'3px 10px', borderRadius:999}}>{info.label}</span>
                        </td>
                        <td style={{textAlign:'right', whiteSpace:'nowrap'}}>
                          <button className="btn btn-secondary" style={{padding:'5px 10px', fontSize:12, marginRight:6}}
                            onClick={()=>abrirEdicion(e)}>✎ Editar</button>
                          <button className="btn btn-secondary" style={{padding:'5px 10px', fontSize:12, marginRight:6}}
                            disabled={generandoLink === e.id} onClick={()=>generarLink(e)}>
                            {generandoLink === e.id ? '...' : '🔗 Link de pago'}
                          </button>
                          {e.estado === 'suspendido' ? (
                            <button className="btn btn-accent" style={{padding:'5px 10px', fontSize:12}}
                              onClick={()=>reactivar(e)}>Reactivar</button>
                          ) : (
                            <button className="btn btn-secondary" style={{padding:'5px 10px', fontSize:12, color:'var(--color-danger)'}}
                              onClick={()=>suspender(e)}>Suspender</button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modal: editar suscripción */}
      {editando && (
        <div onClick={()=>!guardando && setEditando(null)}
          style={{position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:1000,
            display:'flex', alignItems:'center', justifyContent:'center', padding:16}}>
          <div onClick={e=>e.stopPropagation()} className="card" style={{width:'100%', maxWidth:400}}>
            <div className="section-title" style={{marginBottom:2}}>Editar suscripción</div>
            <div style={{fontSize:12, color:'var(--color-muted)', marginBottom:14}}>{editando.nombre}</div>

            <div className="form-group">
              <label className="form-label">Mensualidad</label>
              <input className="form-input" value={form.monto_mensual}
                onChange={e=>setForm({...form, monto_mensual: e.target.value.replace(/[^0-9.,]/g,'')})}/>
            </div>
            <div className="form-group">
              <label className="form-label">Correo de contacto (para el link de pago)</label>
              <input className="form-input" type="email" value={form.email_contacto}
                onChange={e=>setForm({...form, email_contacto: e.target.value})}/>
            </div>
            <div className="form-group">
              <label className="form-label">Próximo pago</label>
              <input className="form-input" type="date" value={form.fecha_proximo_pago}
                onChange={e=>setForm({...form, fecha_proximo_pago: e.target.value})}/>
            </div>

            <div style={{display:'flex', gap:10, marginTop:6}}>
              <button className="btn btn-secondary" style={{flex:1}} disabled={guardando} onClick={()=>setEditando(null)}>Cancelar</button>
              <button className="btn btn-primary" style={{flex:1}} disabled={guardando} onClick={guardar}>
                {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: link de pago generado */}
      {linkListo && (
        <div onClick={()=>setLinkListo(null)}
          style={{position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:1000,
            display:'flex', alignItems:'center', justifyContent:'center', padding:16}}>
          <div onClick={e=>e.stopPropagation()} className="card" style={{width:'100%', maxWidth:440}}>
            <div className="section-title" style={{marginBottom:2}}>Link de pago listo</div>
            <div style={{fontSize:12, color:'var(--color-muted)', marginBottom:14}}>
              Para {linkListo.empresa.nombre} · {fmt(linkListo.empresa.monto_mensual)}
            </div>
            <div style={{background:'var(--color-bg)', borderRadius:8, padding:'10px 12px', fontSize:13, wordBreak:'break-all', marginBottom:14}}>
              {linkListo.url}
            </div>
            <div style={{display:'flex', gap:10}}>
              <button className="btn btn-secondary" style={{flex:1}} onClick={()=>setLinkListo(null)}>Cerrar</button>
              <button className="btn btn-primary" style={{flex:1}} onClick={copiarLink}>Copiar link</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
